/**
 * demoData.ts - jeux de données générés pour le MODE DÉMO.
 *
 * Activé via le flag localStorage `nis365.demoMode` (toggle dans /devmode).
 * Quand le mode démo est actif, les wrappers d'API (azure, cyfun, onboarding,
 * licence) court-circuitent l'appel réseau et renvoient ces données, pour
 * présenter un faux client « Business » avec des données complètes.
 *
 * Le cœur (dashboard, contrôles M365, scans M365) est déjà couvert par les
 * mocks existants (scanStore + mockTenants) quand IS_LIVE_MODE bascule à false.
 */
import { AZURE_CONTROLS } from '../domain/azureControls';
import type { ClientLicenseStatus } from '../api/licenseApi';
import type { AzureConnection } from '../api/azureConnectionsApi';
import type { AzureScan, AzureCheckResult, AzureCheckStatus } from '../api/azureScansApi';
import type { CyFunConfig } from '../api/cyfunApi';
import type { OnboardingStatus } from '../api/onboardingApi';

const DEMO_CLIENT_NAME = 'Contoso Industries';
const DEMO_TENANT_ID   = 'd9f6e1a4-1a2b-4c3d-9e8f-1234567890ab';
const DEMO_SUB_ID      = '8f2a1c4e-7b6d-4a3e-9c8b-1a2b3c4d5e6f';
const DEMO_CONN_ID     = 'demo-az-conn-we';

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}
function daysAgoIso(d: number): string {
  const x = new Date();
  x.setDate(x.getDate() - d);
  return x.toISOString();
}

// ---------------------------------------------------------------------------
// Licence : faux client Business valide.
// ---------------------------------------------------------------------------
export function demoLicense(): ClientLicenseStatus {
  return {
    hasLicense:    true,
    valid:         true,
    expired:       false,
    revoked:       false,
    mode:          'paid',
    plan:          'business',
    validFrom:     daysAgoIso(120),
    validUntil:    daysAgoIso(-245), // ~8 mois dans le futur
    daysRemaining: 245,
    customer:      { companyName: DEMO_CLIENT_NAME, email: 'demo@nis365.eu' },
    activatedAt:   daysAgoIso(120),
    activatedBy:   'demo@nis365.eu',
    message:       null,
  };
}

// ---------------------------------------------------------------------------
// Connexion Azure de démo.
// ---------------------------------------------------------------------------
export function demoAzureConnection(): AzureConnection {
  return {
    id:             DEMO_CONN_ID,
    name:           'Prod — West Europe',
    tenantId:       DEMO_TENANT_ID,
    subscriptionId: DEMO_SUB_ID,
    appClientId:    '00000000-demo-app-0000-000000000000',
    secretRef:      'akv:demo-secret',
    status:         'ok',
    addedAt:        daysAgoIso(90),
    addedBy:        'demo@nis365.eu',
    lastTestAt:     daysAgoIso(1),
    lastScanAt:     daysAgoIso(1),
  };
}

// ---------------------------------------------------------------------------
// Scan Azure de démo : un résultat par contrôle du catalogue (67), réparti de
// façon déterministe pour une posture « Business » réaliste (~70%).
// ---------------------------------------------------------------------------
function demoAzureStatus(controlId: string, manual: boolean): AzureCheckStatus {
  if (manual) return 'manual';
  const r = hash(controlId) % 10;
  if (r < 6) return 'pass';
  if (r < 8) return 'warning';
  if (r === 8) return 'fail';
  return 'pass';
}
function demoAzureMessage(status: AzureCheckStatus, title: string): string {
  switch (status) {
    case 'pass':    return `Conforme — ${title}.`;
    case 'warning': return `À améliorer — ${title} : couverture partielle détectée.`;
    case 'fail':    return `Non conforme — ${title} : action requise.`;
    case 'manual':  return 'Vérification manuelle requise via le portail Azure.';
    default:        return title;
  }
}

export function demoAzureScan(): AzureScan {
  const evaluatedAt = daysAgoIso(1);
  const results: AzureCheckResult[] = AZURE_CONTROLS.map((c) => {
    const status = demoAzureStatus(c.id, !!c.manual);
    const score = status === 'pass' ? 1 : status === 'warning' ? 0.5 : status === 'fail' ? 0 : null;
    return {
      controlId:   c.id,
      status,
      score,
      message:     demoAzureMessage(status, c.title),
      evaluatedAt,
      evidenceUrl: status === 'pass' || status === 'fail' ? 'demo://evidence' : null,
    };
  });
  const scored = results.filter((r) => r.status !== 'manual' && r.status !== 'not_applicable');
  const globalScore = scored.length
    ? Math.round((100 * scored.reduce((s, r) => s + (r.score ?? 0), 0)) / scored.length)
    : 0;
  return {
    id:             'demo-az-scan-1',
    clientId:       'demo-client',
    connectionId:   DEMO_CONN_ID,
    subscriptionId: DEMO_SUB_ID,
    status:         'done',
    startedAt:      daysAgoIso(1),
    finishedAt:     evaluatedAt,
    results,
    globalScore,
  };
}

export function demoAzureScans(): { scans: AzureScan[] } {
  const s1 = demoAzureScan();
  const s2: AzureScan = { ...s1, id: 'demo-az-scan-0', startedAt: daysAgoIso(8), finishedAt: daysAgoIso(8), globalScore: Math.max(0, s1.globalScore - 6) };
  return { scans: [s1, s2] };
}

// ---------------------------------------------------------------------------
// Config CyFun de démo (niveau cible Important).
// ---------------------------------------------------------------------------
export function demoCyfunConfig(): CyFunConfig {
  return { targetLevel: 'Important', scanSnapshot: {} };
}

// ---------------------------------------------------------------------------
// Onboarding status de démo : tenant Business connecté + identité entreprise.
// ---------------------------------------------------------------------------
export function demoOnboardingStatus(): OnboardingStatus {
  return {
    connected:         true,
    needsOnboarding:   false,
    tenantId:          DEMO_TENANT_ID,
    organizationName:  DEMO_CLIENT_NAME,
    consentedAt:       daysAgoIso(90),
    consentedBy:       { userId: 'demo', email: 'admin@contoso.com', name: 'Admin Contoso' },
    lastTestAt:        daysAgoIso(1),
    lastTestSuccess:   true,
    onmsDomain:        'contoso.onmicrosoft.com',
    defaultDomain:     'contoso.com',
    entraIdSku:        'P2',
    userCount:         850,
    notificationLanguage: 'fr-BE',
    companyName:       DEMO_CLIENT_NAME,
    techContact:       'rssi@contoso.com',
    companyLegalName:  'Contoso Industries SA',
    companyBce:        'BE 0123.456.789',
    companyAddress:    'Boulevard de l’Industrie 42',
    companyPostalCode: '4000',
    companyCity:       'Liège',
    companyCountry:    'Belgique',
  };
}
